import React, { useState, useEffect } from "react";
import { Megaphone, Loader2, ExternalLink, CheckCircle2, Linkedin, Link2, Unlink, Rocket, Repeat, LayoutGrid, Sparkles } from "lucide-react";
import { createLeadCampaign, getLeadCampaigns, getLinkedInStatus, connectLinkedIn, disconnectLinkedIn, getAutoPost, setAutoPost, getAutoStudio, setAutoStudio, getRecapSettings, setRecapSettings, generateWeeklyRecap } from "../../api";
import { useToast } from "../../hooks/use-toast";

const AREAS = [
  { label: "Bath (BA1/BA2)", area: "Bath BA1/BA2", lat: 51.3810, lng: -2.3590 },
  { label: "Bristol (BS)", area: "Bristol", lat: 51.4545, lng: -2.5879 },
  { label: "Bath & Bristol", area: "Bath & Bristol", lat: 51.4180, lng: -2.4700 },
];

const AdsLinkedIn = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "Anadear — Lead Generation", dailyBudget: 25, areaIdx: 0, radiusMiles: 15, headline: "Free property valuation", primaryText: "Thinking of moving? Book your free, no-obligation valuation with Anadear Estates." });
  const [creating, setCreating] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [li, setLi] = useState(null);
  const [liBusy, setLiBusy] = useState(false);
  const [autopost, setAutopost] = useState(null);
  const [recap, setRecap] = useState(null);
  const [recapBusy, setRecapBusy] = useState(false);
  const [autostudio, setAutostudio] = useState(null);

  const refresh = () => {
    getLeadCampaigns().then(setCampaigns).catch(() => setCampaigns([]));
    getLinkedInStatus().then(setLi).catch(() => setLi(null));
    getAutoPost().then(setAutopost).catch(() => setAutopost(null));
    getAutoStudio().then(setAutostudio).catch(() => setAutostudio(null));
    getRecapSettings().then(setRecap).catch(() => setRecap(null));
  };
  useEffect(() => { refresh(); }, []);

  const toggleRecap = async () => {
    try { const r = await setRecapSettings({ enabled: !recap?.enabled }); setRecap((p) => ({ ...p, ...r })); toast({ title: r.enabled ? "Weekly recap ON — drafts every Monday" : "Weekly recap off" }); }
    catch { toast({ title: "Couldn't update the rule" }); }
  };
  const runRecap = async () => {
    setRecapBusy(true);
    try { const d = await generateWeeklyRecap(); toast({ title: "Recap carousel drafted", description: `${(d.images || []).length} listings — review it in Marketing → Pending Review.` }); }
    catch (err) { toast({ title: "Couldn't build recap", description: err?.response?.data?.detail || "Add some listings with photos first." }); }
    finally { setRecapBusy(false); }
  };

  const toggleAuto = async () => {
    try { const r = await setAutoPost({ enabled: !autopost?.enabled }); setAutopost(r); toast({ title: r.enabled ? "Auto-post ON — new listings will draft posts" : "Auto-post off" }); }
    catch { toast({ title: "Couldn't update the rule" }); }
  };

  const toggleAutoStudio = async () => {
    try { const r = await setAutoStudio({ enabled: !autostudio?.enabled }); setAutostudio(r); toast({ title: r.enabled ? "Auto-Studio ON — new listings draft a post + advert" : "Auto-Studio off" }); }
    catch { toast({ title: "Couldn't update the rule" }); }
  };

  const toggleAutoStudioImage = async () => {
    try { const r = await setAutoStudio({ generateImage: !autostudio?.generateImage }); setAutostudio(r); toast({ title: r.generateImage ? "AI images ON for Auto-Studio" : "AI images off (uses listing photos)" }); }
    catch { toast({ title: "Couldn't update the rule" }); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const a = AREAS[form.areaIdx];
      await createLeadCampaign({
        name: form.name, dailyBudget: Number(form.dailyBudget), area: a.area, lat: a.lat, lng: a.lng,
        radiusMiles: Number(form.radiusMiles), headline: form.headline, primaryText: form.primaryText,
        link: "https://anadearestates.com/valuation",
      });
      toast({ title: "Campaign created (PAUSED)", description: "Review it in Ads Manager below, then flip it live." });
      refresh();
    } catch (err) { toast({ title: "Couldn't create campaign", description: err?.response?.data?.detail || "Try again." }); }
    finally { setCreating(false); }
  };

  const connectLi = async () => {
    setLiBusy(true);
    try { const r = await connectLinkedIn(); window.location.href = r.url; }
    catch (err) { toast({ title: "LinkedIn not configured", description: err?.response?.data?.detail || "Add LinkedIn app keys first." }); setLiBusy(false); }
  };
  const disconnectLi = async () => {
    setLiBusy(true);
    try { await disconnectLinkedIn(); toast({ title: "LinkedIn disconnected" }); refresh(); }
    catch { toast({ title: "Couldn't disconnect" }); }
    finally { setLiBusy(false); }
  };

  const inputCls = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

  return (
    <div className="space-y-6" data-testid="growth-ads">
      {/* Lead-gen campaign builder */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <div className="flex items-center gap-2 mb-1"><Megaphone size={18} className="text-[#b8935f]" /><h3 className="font-display text-[18px] font-500 text-[#2b2b2b]">Meta lead-gen campaign builder</h3></div>
        <p className="text-[13px] text-[#8a8a8a] mb-5">Spin up a Facebook + Instagram lead-generation campaign with an instant enquiry form. It's created <b>PAUSED</b> — no spend happens until you review and switch it live in Ads Manager.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-[12px] text-[#6a6a6a]">Campaign name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="campaign-name" />
          </div>
          <div>
            <label className="text-[12px] text-[#6a6a6a]">Daily budget (£)</label>
            <input type="number" value={form.dailyBudget} onChange={(e) => setForm({ ...form, dailyBudget: e.target.value })} className={inputCls} data-testid="campaign-budget" />
          </div>
          <div>
            <label className="text-[12px] text-[#6a6a6a]">Target area</label>
            <select value={form.areaIdx} onChange={(e) => setForm({ ...form, areaIdx: Number(e.target.value) })} className={inputCls} data-testid="campaign-area">
              {AREAS.map((a, i) => <option key={a.area} value={i}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] text-[#6a6a6a]">Radius (miles — Meta requires ≥15 for property ads)</label>
            <input type="number" min={15} value={form.radiusMiles} onChange={(e) => setForm({ ...form, radiusMiles: e.target.value })} className={inputCls} data-testid="campaign-radius" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[12px] text-[#6a6a6a]">Headline</label>
            <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="text-[12px] text-[#6a6a6a]">Primary text</label>
            <textarea value={form.primaryText} onChange={(e) => setForm({ ...form, primaryText: e.target.value })} rows={2} className={inputCls + " h-auto py-2"} />
          </div>
        </div>
        <button onClick={create} disabled={creating} data-testid="create-campaign-btn" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">
          {creating ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} Create paused campaign
        </button>
      </div>

      {/* Created campaigns */}
      {campaigns.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
          <h4 className="font-display text-[16px] font-500 text-[#2b2b2b] mb-3">Draft campaigns ({campaigns.length})</h4>
          <div className="space-y-2" data-testid="campaign-list">
            {campaigns.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
                <div>
                  <p className="text-[14px] font-500 text-[#2b2b2b]">{c.name}</p>
                  <p className="text-[12px] text-[#a0a0a0]">{c.area} · £{c.dailyBudget}/day · <span className="text-amber-700">{c.status}</span></p>
                </div>
                {c.reviewUrl && <a href={c.reviewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><ExternalLink size={13} /> Review in Ads Manager</a>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auto-post new listings rule */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="autopost-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1"><Repeat size={18} className="text-[#b8935f]" /><h3 className="font-display text-[18px] font-500 text-[#2b2b2b]">Auto-post new listings</h3></div>
            <p className="text-[13px] text-[#8a8a8a]">When on, every new property you add auto-drafts a Facebook, Instagram and LinkedIn post to <b>Pending Review</b> in Marketing — ready to approve and publish.</p>
          </div>
          <button onClick={toggleAuto} data-testid="autopost-toggle" role="switch" aria-checked={!!autopost?.enabled}
            className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${autopost?.enabled ? "bg-green-600" : "bg-[#e2d8c8]"}`}>
            <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition-transform ${autopost?.enabled ? "translate-x-6" : ""}`} />
          </button>
        </div>
      </div>

      {/* Auto-Studio: full post + advert per new listing */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="autostudio-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1"><Sparkles size={18} className="text-[#b8935f]" /><h3 className="font-display text-[18px] font-500 text-[#2b2b2b]">Auto-Studio for new listings</h3></div>
            <p className="text-[13px] text-[#8a8a8a]">When on, every new instruction is turned into a ready <b>social post</b> and a <b>Meta advert</b> (headline, primary text, CTA &amp; audience) — queued to <b>Pending Review</b> in Marketing, ready to approve and publish.</p>
          </div>
          <button onClick={toggleAutoStudio} data-testid="autostudio-toggle" role="switch" aria-checked={!!autostudio?.enabled}
            className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${autostudio?.enabled ? "bg-green-600" : "bg-[#e2d8c8]"}`}>
            <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition-transform ${autostudio?.enabled ? "translate-x-6" : ""}`} />
          </button>
        </div>
        {autostudio?.enabled && (
          <label className="flex items-center gap-2 mt-4 pt-4 border-t border-[#f0ece5] cursor-pointer" data-testid="autostudio-image-row">
            <button type="button" onClick={toggleAutoStudioImage} role="switch" aria-checked={!!autostudio?.generateImage}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${autostudio?.generateImage ? "bg-[#b8935f]" : "bg-[#e2d8c8]"}`}>
              <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${autostudio?.generateImage ? "translate-x-5" : ""}`} />
            </button>
            <span className="text-[13px] text-[#4a4a4a]">Also create an AI image for each <span className="text-[#a0a0a0]">(uses LLM credits; otherwise uses the listing's own photo)</span></span>
          </label>
        )}
      </div>

      {/* Weekly recap carousel */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="recap-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1"><LayoutGrid size={18} className="text-[#b8935f]" /><h3 className="font-display text-[18px] font-500 text-[#2b2b2b]">Weekly recap carousel</h3></div>
            <p className="text-[13px] text-[#8a8a8a]">Every Monday, rounds up the week's new instructions into one branded multi-image carousel post, queued to <b>Pending Review</b> in Marketing.</p>
            {recap?.lastRun && <p className="text-[11px] text-[#a0a0a0] mt-1">Last run: {new Date(recap.lastRun).toLocaleString("en-GB")}</p>}
          </div>
          <button onClick={toggleRecap} data-testid="recap-toggle" role="switch" aria-checked={!!recap?.enabled}
            className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${recap?.enabled ? "bg-green-600" : "bg-[#e2d8c8]"}`}>
            <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition-transform ${recap?.enabled ? "translate-x-6" : ""}`} />
          </button>
        </div>
        <button onClick={runRecap} disabled={recapBusy} data-testid="recap-generate-btn" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1c1c1e] text-white text-[13px] font-500 hover:bg-black transition-colors disabled:opacity-60">
          {recapBusy ? <Loader2 size={15} className="animate-spin" /> : <LayoutGrid size={15} />} Generate this week's recap now
        </button>
      </div>

      {/* LinkedIn connection */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <div className="flex items-center gap-2 mb-1"><Linkedin size={18} className="text-[#0A66C2]" /><h3 className="font-display text-[18px] font-500 text-[#2b2b2b]">LinkedIn Company Page</h3></div>
        {!li ? <p className="text-[13px] text-[#8a8a8a]">Loading…</p> : !li.configured ? (
          <p className="text-[13px] text-[#8a8a8a]">LinkedIn isn't configured yet. Add your LinkedIn app <b>Client ID</b> and <b>Client Secret</b> to enable posting to the Anadear Estates page.</p>
        ) : li.connected ? (
          <div>
            <p className="text-[13px] text-green-700 flex items-center gap-1.5 mb-3"><CheckCircle2 size={15} /> Connected{li.organization ? ` — ${li.organization}` : ""}. AI drafts can now publish to LinkedIn.</p>
            <button onClick={disconnectLi} disabled={liBusy} data-testid="linkedin-disconnect" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#e2d8c8] text-[13px] text-[#6a6a6a] hover:border-red-400 hover:text-red-500 transition-colors disabled:opacity-60"><Unlink size={14} /> Disconnect</button>
          </div>
        ) : (
          <div>
            <p className="text-[13px] text-[#8a8a8a] mb-3">Connect the Anadear Estates LinkedIn page so the AI can post there alongside Facebook and Instagram.</p>
            <button onClick={connectLi} disabled={liBusy} data-testid="linkedin-connect" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A66C2] text-white text-[13px] font-500 hover:opacity-90 transition-opacity disabled:opacity-60">{liBusy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Connect LinkedIn</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdsLinkedIn;
